import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Search } from "lucide-react";

interface PoultrySelectorProps {
  stockItems: any[];
  onPoultrySelect: (selectedPoultry: any[]) => void;
}

export const PoultrySelector = ({ stockItems, onPoultrySelect }: PoultrySelectorProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [quantities, setQuantities] = useState<{ [key: string]: number }>({});

  const filteredItems = stockItems.filter(
    (item) => 
      item.data.Race?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.data.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleQuantityChange = (itemId: string, quantity: number) => {
    if (quantity > 0) {
      setQuantities({ ...quantities, [itemId]: quantity });
    }
  };

  const handleAddPoultry = (item: any) => {
    if (quantities[item.id] && quantities[item.id] <= item.quantity) {
      const newItem = {
        ...item,
        selectedQuantity: quantities[item.id]
      };
      setSelectedItems([...selectedItems, newItem]);
      setQuantities({ ...quantities, [item.id]: 0 });
    }
  };

  const handleRemovePoultry = (itemId: string) => {
    setSelectedItems(selectedItems.filter(item => item.id !== itemId));
  };

  const calculateTotal = (item: any) => {
    const quantity = item.selectedQuantity || 0;
    const price = item.data?.price || 0;
    return quantity * price;
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher une volaille..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-8"
        />
      </div>

      {searchQuery && (
        <div className="space-y-2">
          {filteredItems.map((item) => (
            <Card key={item.id} className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{item.data.Race || item.data.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Stock: {item.quantity} | Prix: {item.data.price?.toLocaleString()} FCFA
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    max={item.quantity}
                    value={quantities[item.id] || ""}
                    onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value))}
                    className="w-32 text-lg [&::-webkit-inner-spin-button]:opacity-100 [&::-webkit-outer-spin-button]:opacity-100"
                  />
                  <Button 
                    onClick={() => handleAddPoultry(item)}
                    disabled={!quantities[item.id] || quantities[item.id] > item.quantity}
                  >
                    Ajouter
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {selectedItems.length > 0 && (
        <div className="space-y-2">
          <Label>Volailles sélectionnées</Label>
          {selectedItems.map((item) => (
            <Card key={item.id} className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{item.data.Race || item.data.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Quantité: {item.selectedQuantity} | Total: {calculateTotal(item).toLocaleString()} FCFA
                  </p>
                </div>
                <Button variant="destructive" onClick={() => handleRemovePoultry(item.id)}>
                  Retirer
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Button 
        className="w-full" 
        disabled={selectedItems.length === 0}
        onClick={() => onPoultrySelect(selectedItems)}
      >
        Valider la sélection
      </Button>
    </div>
  );
};